import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Bin, histogram, max, min } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { area } from 'd3-shape';
import { ObservableInput } from 'observable-input';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Size } from '../core/size.service';

const PAD_PROPORTION = 0.05;
const MIN_TICKS = 1;
const MAX_TICKS = 13;

@Component({
  selector: 'histogram',
  templateUrl: './histogram.component.html',
  styleUrls: ['./histogram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistogramComponent<T> {
  @ObservableInput()
  @Input('data')
  _data!: Observable<T[] | null>;
  readonly data = this._data.pipe(filter(data => data !== null));

  @ObservableInput()
  @Input()
  calculateBin!: Observable<(datum: T) => number>;

  @ObservableInput()
  @Input()
  calculateValue!: Observable<(data: T[]) => number>;

  @ObservableInput()
  @Input()
  binFormatter!: Observable<(value: number) => string>;

  readonly svgSize = new Subject<Size>();

  private readonly leftOffset = of(32);

  readonly xScale = combineLatest([
    this.data,
    this.calculateBin,
    this.svgSize,
    this.leftOffset,
  ]).pipe(
    map(([data, calculateBin, size, offset]) => {
      const binValues = data!.map(d => calculateBin(d));
      const minValue = min(binValues)!;
      const maxValue = max(binValues)!;
      const range = maxValue - minValue;
      const padding = (range * PAD_PROPORTION) / 2;
      return scaleLinear()
        .domain([minValue - padding, maxValue + padding])
        .range([offset, size.width]);
    }),
  );

  readonly xTicks = this.xScale.pipe(
    map(scale => {
      const [minValue, maxValue] = scale.domain();
      return scale.ticks(
        Math.max(
          Math.min(Math.round(maxValue - minValue), MAX_TICKS),
          MIN_TICKS,
        ),
      );
    }),
  );

  private readonly histogram: Observable<Bin<T, number>[]> = combineLatest([
    this.data,
    this.xTicks,
    this.calculateBin,
  ]).pipe(
    map(([data, ticks, calculateBin]) => {
      return histogram<T, number>()
        .value(d => calculateBin(d))
        .thresholds(ticks)(data!);
    }),
  );

  private readonly totalValue = combineLatest([
    this.data,
    this.calculateValue,
  ]).pipe(map(([data, calculateValue]) => calculateValue(data!)));

  private readonly calculateProportion = combineLatest([
    this.totalValue,
    this.calculateValue,
  ]).pipe(
    map(
      ([total, calculateValue]) =>
        (d: T[]) =>
          calculateValue(d) / total,
    ),
  );

  private readonly bottomOffset = of(16);

  readonly yScale = combineLatest([
    this.histogram,
    this.calculateProportion,
    this.svgSize,
    this.bottomOffset,
  ]).pipe(
    map(([bins, calculateProportion, size, offset]) =>
      scaleLinear()
        .domain([
          0,
          max(bins, d => calculateProportion(d) * (1 + PAD_PROPORTION))!,
        ])
        .range([size.height - offset, 0]),
    ),
  );

  readonly yTicks = this.yScale.pipe(map(scale => scale.ticks(6)));

  readonly areaGenerator = combineLatest([
    this.xScale,
    this.yScale,
    this.calculateProportion,
  ]).pipe(
    map(([xScale, yScale, calcY]) =>
      area<Bin<T, number>>()
        .x(d => xScale(d.x0!)!)
        .y0(() => yScale(0)!)
        .y1(d => yScale(calcY(d))!),
    ),
  );

  readonly areaPath = combineLatest([this.histogram, this.areaGenerator]).pipe(
    map(([bins, generator]) => generator(bins)),
  );

  readonly linePath = combineLatest([this.histogram, this.areaGenerator]).pipe(
    map(([bins, generator]) => generator.lineY1()(bins)),
  );

  readonly markers: Observable<{ x: number; y: number; value: number }[]> =
    combineLatest([
      this.histogram,
      this.calculateProportion,
      this.xScale,
      this.yScale,
      this.binFormatter,
    ]).pipe(
      map(([bins, calculateProportion, xScale, yScale, binFormatter]) =>
        bins
          .filter(bin => bin.length > 0)
          .map(bin => ({
            x: xScale(bin.x0!)!,
            y: yScale(calculateProportion(bin))!,
            value: calculateProportion(bin),
          })),
      ),
    );
}
